import { getAuthRequestHeaders } from "@qw/auth";
import { useTranscriptionContextLazyQuery } from "@qw/graphql";
import { useToast } from "@qw/ui";
import { useCallback, useEffect } from "react";

import { useStreamingTranscription } from "../../hooks/use-streaming-transcription";

// Timeout for fetching transcription context (vocabulary hints)
const TRANSCRIPTION_CONTEXT_TIMEOUT_MS = 5000;

export interface UseChatTranscriptionOptions {
  apiUrl?: string;
  chatSessionId: string;
  onTranscriptUpdate?: (text: string) => void;
  onTranscriptionError?: (error: Error) => void;
}

export const useChatTranscription = (options?: UseChatTranscriptionOptions) => {
  const { apiUrl, chatSessionId, onTranscriptUpdate, onTranscriptionError } = options ?? {};

  const { toast } = useToast();

  const [fetchTranscriptionContext] = useTranscriptionContextLazyQuery({
    fetchPolicy: "cache-first",
  });

  // Fetch transcription context (vocabulary hints) using Apollo with caching
  const getTranscriptionPrompt = useCallback(async (): Promise<string | null> => {
    if (!chatSessionId) {
      return null;
    }

    try {
      const result = await fetchTranscriptionContext({
        variables: { chatSessionId },
        context: { timeout: TRANSCRIPTION_CONTEXT_TIMEOUT_MS },
      });

      return result.data?.transcriptionContext?.prompt ?? null;
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional
      console.warn("Error fetching transcription context:", error);
      return null;
    }
  }, [chatSessionId, fetchTranscriptionContext]);

  const transcribeAudio = useCallback(
    async (audioBlob: Blob) => {
      // Get cached transcription prompt (fetches once on first call, Apollo caches)
      const prompt = await getTranscriptionPrompt();

      const formData = new FormData();
      const extension = audioBlob.type.split("/")[1]?.split(";")[0] || "webm";
      formData.append("file", audioBlob, `voice-input.${extension}`);

      const baseUrl = apiUrl || location.origin;
      const url = new URL(`${baseUrl}/api/alby/voice/transcribe`);
      if (chatSessionId) {
        url.searchParams.set("chatSessionId", chatSessionId);
      }
      if (prompt) {
        url.searchParams.set("prompt", prompt);
      }

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: getAuthRequestHeaders(),
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to transcribe audio");
      }

      const data = (await response.json()) as { text: string };
      return data.text;
    },
    [apiUrl, chatSessionId, getTranscriptionPrompt]
  );

  // Handle transcription errors
  const handleTranscriptionError = useCallback(
    (error: Error) => {
      onTranscriptionError?.(error);
      toast({
        title: "Transcription error",
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      });
    },
    [toast, onTranscriptionError]
  );

  // Streaming transcription with VAD-based chunking
  const streamingTranscription = useStreamingTranscription({
    transcribeAudio,
    onTranscriptUpdate,
    onError: handleTranscriptionError,
  });

  const {
    state,
    audioLevels,
    startRecording: startTranscriptionRecording,
    stopRecording: stopTranscriptionRecording,
    cancelRecording: cancelTranscriptionRecording,
  } = streamingTranscription;

  const isIdle = state === "idle";
  const isRecording = state === "recording";
  const isProcessing = state === "processing";
  const isError = state === "error";

  // Start recording when mic button is clicked
  const startRecording = useCallback(async () => {
    if (isIdle || isError) {
      // Prefetch transcription context in parallel with recording start
      void getTranscriptionPrompt();

      await startTranscriptionRecording();
    }
  }, [isIdle, isError, startTranscriptionRecording, getTranscriptionPrompt]);

  // Stop recording (streaming transcription handles the rest)
  const stopRecording = useCallback(async () => {
    if (!isRecording) {
      return;
    }

    const finalTranscript = await stopTranscriptionRecording();

    return finalTranscript;
  }, [isRecording, stopTranscriptionRecording]);

  // Cancel recording on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isRecording) {
        cancelTranscriptionRecording();
      }
    };

    globalThis.addEventListener("keydown", handleKeyDown);
    return () => globalThis.removeEventListener("keydown", handleKeyDown);
  }, [isRecording, cancelTranscriptionRecording]);

  return {
    audioLevels,
    startRecording,
    stopRecording,
    cancelRecording: cancelTranscriptionRecording,
    isIdle,
    isRecording,
    isProcessing,
    isError,
  };
};
